import { makeAutoObservable } from 'mobx';
import RootStore from './RootStore';

interface INotification {
  key: string;
  message: string;
  options?: {
    variant?: string;
    persist?: boolean;
    autoHideDuration?: number;
    action?: React.ReactNode;
    onClick?: () => void;
    onExited?: () => void;
    onClose?: (event: React.SyntheticEvent<unknown>, reason: string) => void;
  };
  value?: string;
}

interface IStandardNotification {
  key: string;
  value: string;
}

class NotificationStore {
  rootStore;
  debugMode = false;

  notifications: INotification[] = [];

  standardNotifications: IStandardNotification[] = [];

  displayed: INotification[] = [];

  notiRef: React.RefObject<unknown> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  storeDisplayed = (
    /** @type {{ key: string; }} */ notification: INotification
  ) => {
    this.debugMode && console.warn('Debug storeDisplayed', notification);
    this.displayed = [...this.displayed, { ...notification }];
  };

  generateUniqueNotificationId = () => {
    const currentKeyTime = new Date().getTime();

    return `${currentKeyTime}${this.notifications.length}`;
  };

  addStandardNotification = (
    /** @type {string} */ standardNotificationName: string
  ) => {
    if (!standardNotificationName)
      throw new Error('Notifications require a message');
    this.debugMode &&
      console.warn(
        'Debug addStandardNotification standardNotificationName',
        standardNotificationName
      );
    const uniqueKey = this.generateUniqueNotificationId();
    this.standardNotifications.push({
      key: uniqueKey,
      value: standardNotificationName,
    });
    return uniqueKey;
  };

  addNotification = ({
    message,
    options = {},
  }: {
    message: string;
    options?: object;
  }) => {
    if (!message) throw new Error('Notifications require a message');
    this.debugMode &&
      console.warn('Debug addStandardNotification message', message);
    this.debugMode &&
      console.warn('Debug addStandardNotification options', options);
    const uniqueKey = this.generateUniqueNotificationId();
    this.notifications.push({
      key: uniqueKey,
      message,
      options,
    });
    return uniqueKey;
  };

  addSuccessNotification = (/** @type {string} */ message: string) => {
    if (!message) throw new Error('Notifications require a message');
    return this.addNotification({
      message: message,
      options: { variant: 'success', autoHideduration: 4000 },
    });
  };

  addInfoNotification = (/** @type {string} */ message: string) => {
    if (!message) throw new Error('Notifications require a message');
    return this.addNotification({
      message: message,
      options: { variant: 'info', autoHideduration: 4000 },
    });
  };

  addWarningNotification = (/** @type {string} */ message: string) => {
    if (!message) throw new Error('Notifications require a message');
    return this.addNotification({
      message: message,
      options: { variant: 'warning', autoHideduration: 4000 },
    });
  };

  addErrorNotification = (/** @type {string} */ message: string) => {
    if (!message) throw new Error('Notifications require a message');
    return this.addNotification({
      message: message,
      options: { variant: 'error', autoHideduration: 4000 },
    });
  };

  addNotificationRefKey = (
    /** @type {string} */ key: string,
    /** @type {string} */ snackbarKey: string
  ) => {
    this.debugMode &&
      console.warn(
        'Debug addNotificationRefKey key, snackbarKey',
        key,
        snackbarKey
      );
    const foundIndex = this.displayed.findIndex(
      (displayedNotification) => displayedNotification.key === key
    );
    if (foundIndex > -1) {
      this.displayed[foundIndex] = {
        ...this.displayed[foundIndex],
        key: snackbarKey,
      };
    }
  };

  removeNotification = (/** @type {string} */ key: string) => {
    this.debugMode && console.warn('Debug removeNotification key', key);
    this.notifications = this.notifications
      .slice()
      .filter((notification) => notification.key !== key);
  };

  removeStandardNotification = (/** @type {string} */ value: string) => {
    this.debugMode &&
      console.warn('Debug removeStandardNotification value', value);
    this.standardNotifications = this.standardNotifications
      .slice()
      .filter((notification) => notification.value !== value);
  };

  setRef = (ref: React.RefObject<unknown> | null) => {
    this.debugMode && console.warn('Debug setRef', ref);
    this.notiRef = ref;
  };
}

export default NotificationStore;
